import BaseChart from './BaseChart'
import ItemsHelper from '../chart_helpers/ItemsHelper'
import RootHelper from '../chart_helpers/RootHelper'
import BrushHelper from '../chart_helpers/BrushHelper'

import AxisHelper, {
  cleanCollisions,
  continuousExtentParts,
  padLinear,
  continuousExtentTicks,
} from '../chart_helpers/AxisHelper'
import { continuousExtent, categoricalExtent } from '../chart_helpers/AxisHelper'

import SelectableHelper from '../chart_helpers/SelectableHelper'
import * as COLORS from '../utils/colorUtils'
import { max as d3_max, min as d3_min } from 'd3-array'

import {
  curveCardinal as d3_curveCardinal,
  curveBasis as d3_curveBasis,
  curveMonotoneX as d3_curveMonotoneX,
  curveLinear as d3_curveLinear,
  line as d3_line,
  area as d3_area,
} from 'd3-shape'
import { interpolateRgb as d3_interpolateRgb } from 'd3-interpolate'
import { select as d3_select } from 'd3-selection'
import SizeHelper from '../chart_helpers/SizeHelper'
import { getCustomLineLabelHtml, layoutLabels } from '../utils/labelUtils'
import TrendLinesHelper from '../chart_helpers/TrendLinesHelper'
import {
  drawSvgCircle,
  drawSvgPlus,
  drawSvgPolygon,
  drawSvgSquare,
  drawSvgStar,
  drawSvgTriangle,
} from '../utils/shapeUtils'
import { isDarkMode } from '../utils/configUtils'
import { ACCESSIBILITY_OPTIONS_KEYS } from '../utils/accessibilityUtils'

function getCurveType(config) {
  if (config.curve === 'smooth') return d3_curveCardinal
  else if (config.curve === 'smooth-b') return d3_curveBasis
  else if (config.curve === 'smooth-m') return d3_curveMonotoneX
  else return d3_curveLinear
}
function colorToGradId(color) {
  let str = `grd-${parseInt(color.replace('#', '0x'))}`
  return str
}
function updateGradientDefs(root, colors) {
  let defs = d3_select(root).selectAll('defs').data([null])
  defs = defs.merge(defs.enter().append('defs'))
  let grads = defs.selectAll('linearGradient').data(colors, d => d)
  grads.exit().remove()
  grads
    .enter()
    .append('linearGradient')
    .attr('id', d => colorToGradId(d))
    .attr('x2', '0%')
    .attr('y2', '100%')
    .each(function (color) {
      d3_select(this)
        .append('stop')
        .attr('offset', '0%')
        .attr('style', `stop-color:${color};stop-opacity:0.2`)
      d3_select(this)
        .append('stop')
        .attr('offset', '100%')
        .attr('style', `stop-color:${color};stop-opacity:0`)
    })
}
export default class LineChartChart extends BaseChart {
  constructor(...args) {
    super(...args)
    if (args[1]?.mode === 'areas') this.zeroBased = true
  }
  static defaultConfig = {
    /**
     * {-EN-} Type of curve. `smooth` curves will interpolate between the data points. `shapr` curves will be composed of straight line segments. `smooth-m` preserves monotonicity in y, assuming monotonicity in x.
     * {-ES-} Tipo de curva. Las curvas `smooth` se interpolarán entre los puntos de datos. Las curvas `shapr` estarán compuestas por segmentos de línea recta. `smooth-m` conserva la monotonicidad en y, asumiendo monotonicidad en x.
     */
    curve: {
      type: String,
      default: 'smooth',
      options: ['smooth', 'sharp' /*'smooth-b'*/, 'smooth-m'],
      exposed: true,
    },
    /**
     * {-EN-} Creates a continuous line between a set of data points, even if some points have missing or `null` values in between.
     * {-ES-} Crea una línea continua entre un conjunto de puntos de datos, incluso si algunos puntos tienen valores faltantes o `null` entre ellos.
     */
    interpolateLine: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    /**
     * {-EN-} Show/hide the data points that form each line.
     * {-ES-} Muestra/oculta los puntos de datos que forman cada línea.
     */
    showPoints: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    /**
     * {-EN-} Define the data point size that form each line.
     * {-ES-} Define el tamaño los puntos de datos que forman cada línea.
     */
    pointsSize: {
      type: [Number, String],
      default: 3,
      options: ['hidden', 3, 6, 9],
      exposed: true,
    },
    showLineLabel: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    /**
     * {-EN-} Define a preset of how line label will be rendered.
     * {-ES-} Define un ajuste preestablecido de cómo se representará la etiqueta de la línea
     */
    lineLabelPreset: {
      type: String,
      default: 'default',
      options: ['default', 'lineDate', 'lineValue', 'lineDateValue'],
      exposed: true,
    },
    /**
     * {-EN-} Customize line chart labels content.
     * {-ES-} Personaliza el contenido de las etiquetas del gráfico de líneas
     */
    getLineLabelHtml: {
      type: [Function, null],
      exposed: true,
      default: null,
      /*
      function(d) {
        return `
          <div>
            ${this.color?.(d)}
          <div>${d.id}</div>
          <div>${d.value[d.value.length - 1].value}</div>
          <div>${this.formatValue?.(d.value[4].id, 'x')}</div>
          </div>
        `
      },
      */
    },
    /**
     * {-EN-} Show/hide line icons
     * {-ES-} Mostrar/ocultar iconos de líneas
     */
    showLineIcons: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    showDatumValues: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    gradientFill: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    highlightNodeOnClick: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    AxisHelper: {
      zoom: {
        nonEditable: true,
      },
      scroll: {
        default: false,
        exposed: true,
      },
      showScrollbar: {
        default: true,
        exposed: true,
      },
      x: {
        getExtent: function (data) {
          if (this.axes.x.scale.type === 'scalePoint') return categoricalExtent(data)
          else return continuousExtent(data, d => d.id)
        },
        getDataType: function (data) {
          let ticks = continuousExtentTicks(data, d => d.id)
          if (ticks.every(n => n.getTime)) return Date
          else if (ticks.some(n => isNaN(Number(n)) || String(n).replaceAll(' ', '') !== String(n)))
            return String
          else return null
        },
        // valueFormat: '[D]%d %b'
      },
      y: {
        getExtent: function (data) {
          let extent, extentParts
          //Check if domain is set manually and return domain [min, max]
          if (this.config.AxisHelper.y.domain[0] !== 'auto') {
            return this.config.AxisHelper.y.domain
          }
          if (this.config.mode === 'lines') {
            extentParts = continuousExtentParts(data)
            // console.log("extent parts",extentParts)
            extent = continuousExtent(extentParts)
          } else {
            extentParts = continuousExtentParts(data, d => [d.y0, d.y1])
            // console.log("extent parts",extentParts)
            extent = continuousExtent(extentParts)
          }
          //const originalExtent = [...extent]
          if (extent[0] == extent[1]) extent = padLinear(extent, 0.2)
          else if (this.config.AxisHelper.y.showTickValues && this.config.AxisHelper.y.unit) {
            extent = cleanCollisions(this.axes.y, extent, extentParts)
          }

          //if (originalExtent[0] > 0) extent[0] = 0
          return extent
        },
      },
    },
    TrendLinesHelper: {
      /*
      linesDict: {
        value: {
          line_1: {
            serie: 'all',
            type: 'min',
            label: 'Trend line 1',
            color: '#CC0000',
            polynomialOrder: '3',
          },
        },
      },
      */
      getCategoriesData: function () {
        const reducedData = this.data.map(currentLine => currentLine.value)
        return reducedData.flat()
      },
    },
    highlightMaxValue: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    highlightMinValue: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    highlightTotalMinMaxValue: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    /*
     * {-EN-} Highlight an specific point by id
     * {-ES-} Resalta un punto específico por id.
     */
    highlightedPoint: {
      type: [String, Number, null],
      default: null,
      exposed: true,
    },
    highlightedPointSize: {
      type: Number,
      default: 8,
      exposed: true,
    },
    highlightedPointStroke: {
      type: Number,
      default: 3,
      exposed: true,
    },
    highlightedNodeLines: {
      type: [String, Number],
      default: 'all',
      exposed: true,
    },
  }
  static helpers = [
    RootHelper,
    ItemsHelper,
    SelectableHelper,
    SizeHelper,
    AxisHelper,
    BrushHelper,
    TrendLinesHelper,
    // {
    //   class: AxisHelper,
    //   config: {
    //     x:{
    //       getExtent: function (data){
    //         let extent = continuousExtent(data,(d)=>d.id)
    //         // if(this.getValueType('x')==='D' && this.config.AxisHelper.x.scale==='linear') extent = padLinear(extent,0.2)
    //         return extent
    //       },
    //       // valueFormat: '[D]%d %b'
    //     },
    //     y:{
    //       getExtent: function (data){
    //         let extent,extentParts
    //         if(this.config.mode==='lines'){
    //           extentParts = continuousExtentParts(data)
    //           // console.log("extent parts",extentParts)
    //           extent = continuousExtent(extentParts)

    //         } else {
    //           extentParts = continuousExtentParts(data,(d)=>[d.y0,d.y1])
    //           // console.log("extent parts",extentParts)
    //           extent = continuousExtent(extentParts)
    //         }

    //         // extent = padLinear(extent,0.2)

    //         extent = cleanCollisions(this.axes.y, extent, extentParts)

    //         return extent

    //         // if(originalExtent[0]>0) extent[0]=0

    //       }
    //     }
    //   }
    // },
  ]
  checkConsistency() {
    return true
  }
  draw() {
    function fillTween(d) {
      const fill = self.fill(d)
      if (fill?.includes('url'))
        return function (t) {
          return fill
        }
      else {
        const currentFill = d3_select(this).attr('fill')
        var i = d3_interpolateRgb(currentFill, fill)
        return function (t) {
          return i(t)
        }
      }
    }
    if (this.config.mode === 'lines') {
      let line = d3_line()
        .defined(d => d.id !== null && d.value !== null)
        .curve(getCurveType(this.config))
        .x(d => this.axes.x.func.scale()(d.id))
        .y(d => this.axes.y.func.scale()(d.value))

      //shadow area
      let y0s = this.data.map(d => ({ id: d.id, y0: d3_min(d.value, dd => dd.value) }))

      let area = d3_area()
        .defined(d => d.id !== null && d.value !== null)
        .curve(getCurveType(this.config))
        .x(d => this.axes.x.func.scale()(d.id))
        .y0(d => this.axes.y.func.scale()(y0s.find(dd => dd.id === d.parentId).y0))
        .y1(d => this.axes.y.func.scale()(d.value))
      // this.itemsEnter.append('path')
      this.parentItemsEnter.append('path').attr('class', 'line').attr('fill', 'none')
      this.parentItemsEnter.append('path').attr('class', 'shadow').style('pointer-events', 'none')
      // this.itemsEnter.call(drawItem,'enter')
      // this.parentItemsEnter.select('path').attr('d',(d)=>line(d.value)).attr('stroke',(d)=>this.color(d))

      // this.itemsEnterTrans.call(drawItem)
      let self = this
      this.parentItemsEnter
        .select('.line')
        .attr('d', d => line(d.value))
        .attr('stroke', d => this.color(d))
        .attr('stroke-width', d => this.thickness(d))
        // .attr('stroke-dasharray',(d)=>this.strokeDasharray(d))
        .each(function () {
          this._totalLength = this.getTotalLength()
        })
        .attr('stroke-dasharray', function (d) {
          var dashing = self.strokeDasharray(d)
          if (dashing === 'none') dashing = '1 0'
          var dashLength = dashing
            .split(/[\s,]/)
            .map(function (a) {
              return parseFloat(a) || 0
            })
            .reduce(function (a, b) {
              return a + b
            })
          var dashCount = Math.ceil(this._totalLength / dashLength)
          var newDashes = new Array(dashCount).join(dashing + ' ')
          var dashArray = newDashes + ' 0, ' + this._totalLength
          return dashArray
        })
        .attr('stroke-dashoffset', function () {
          return this._totalLength
        })

      this.parentItemsEnter
        .select('.shadow')
        .style('opacity', 0)
        .attr('d', d => area(d.value))
        .attr('fill', d => {
          if (this.config.gradientFill) return `url(#${colorToGradId(this.color(d))})`
          else return 'none'
        })

      this.parentItemsEnterTrans.select('.line').attr('stroke-dashoffset', 0)
      this.parentItemsEnterTrans.select('.shadow').style('opacity', 1)

      this.parentItems.select('.line').attr('stroke-dasharray', 'none')

      updateGradientDefs(
        this.$root,
        this.config.gradientFill ? this.data.map(d => this.color(d)) : []
      )

      this.parentItemsTrans
        .select('.line')
        .attr('d', d => line(d.value))
        .attr('stroke', d => this.color(d))
        .attr('stroke-dasharray', d => this.strokeDasharray(d))
        .attr('stroke-width', d => this.thickness(d))

      this.parentItemsTrans
        .select('.shadow')
        .style('opacity', 1)
        .attr('d', d => area(d.value))
        .attr('fill', d => {
          if (this.config.gradientFill) return `url(#${colorToGradId(this.color(d))})`
          else return 'none'
        })

      this.parentItemsExitTrans.style('opacity', 0)
    } else {
      let area = d3_area()
        .curve(getCurveType(this.config))
        .x(d => {
          return this.axes.x.func.scale()(d.id)
        })
        .y0(d => this.axes.y.func.scale()(d.y0))
        .y1(d => this.axes.y.func.scale()(d.y1))
      let line = d3_line()
        .defined(d => d.value !== null)
        .curve(getCurveType(this.config))
        .x(d => this.axes.x.func.scale()(d.id))
        .y(d => this.axes.y.func.scale()(d.y1))
      this.parentItemsEnter.append('path').attr('class', 'area')
      this.parentItemsEnter.append('path').attr('class', 'separator')
      // this.parentItemsEnter.append('path')
      // this.parentItems.select('path').attr('d',(d)=>area(d.value)).attr('fill',(d)=>this.color(d))
      this.parentItemsEnter
        .select('.area')
        .attr('d', d => {
          return area(
            d.value.map(dd => {
              return { id: dd.id, y0: 0, y1: 0 }
            })
          )
        })
        .attr('fill', d => this.color(d))
      this.parentItemsEnterTrans
        .select('.area')
        .attr('d', d => area(d.value))
        .attrTween('fill', fillTween)

      this.parentItemsTrans
        .select('.area')
        .attr('d', d => area(d.value))
        .attrTween('fill', fillTween)

      this.parentItemsEnter
        .select('.separator')
        .attr('d', d => {
          return line(
            d.value.map(dd => {
              return { id: dd.id, y0: 0, y1: 0 }
            })
          )
        })
        .attr('fill', 'none')
        .attr('stroke', (d, i, arr) => (i < arr.length - 1 ? '#fff' : 'none'))
        .attr('stroke-width', 2)
      this.parentItemsEnterTrans
        .select('.separator')
        .attr('d', d => line(d.value))
        .attr('stroke', (d, i, arr) => (i < arr.length - 1 ? '#fff' : 'none'))
      this.parentItemsTrans
        .select('.separator')
        .attr('d', d => line(d.value))
        .attr('stroke', (d, i, arr) => (i < arr.length - 1 ? '#fff' : 'none'))

      this.parentItemsExitTrans.style('opacity', 0)

      this.parentItems.merge(this.parentItemsEnter).sort((a, b) => b.value[0].y0 - a.value[0].y0)
    }

    let items = this.items.merge(this.itemsEnter)

    let breakpoints = items
      .raise()
      .selectAll('.break-point')
      .data(d => (this.config.showPoints && (d.value || d.value == 0) ? [d] : []))
    // .data(d=>[d])
    breakpoints.exit().transition().style('opacity', 0).remove()
    let breakpointsEnter = breakpoints.enter().append('g').attr('class', 'break-point')

    //dots to enlarge click area
    //breakpointsEnter.append('circle').attr('r', 0).attr('opacity', 0)

    breakpointsEnter.attr(
      'transform',
      d =>
        `translate(${this.axes.x.func.scale()(d.id)},${this.axes.y.func.scale()(d.y1 || d.value)})`
    )

    breakpoints = breakpoints.merge(breakpointsEnter)

    const getPointType = d => {
      if (
        !this.config.ItemsHelper.colorDict ||
        !this.config.ItemsHelper.colorDict[d.parentId]?.shape
      )
        return 'circle'
      return this.config.ItemsHelper.colorDict[d.parentId].shape
    }

    const isDotHighlighted = d => {
      const interactivityHighlightedNode =
        this.config.highlightNodeOnClick &&
        this._interactiveHighlightedNodes?.[d?.parentId]?.toString() === d?.id.toString()
      return (
        interactivityHighlightedNode ||
        (!this.config.highlightNodeOnClick &&
          (this.config.highlightedNodeLines === 'all' ||
            this.config.highlightedNodeLines === d.parentId) &&
          this.config.highlightedPoint &&
          d?.id.toString() === this.config.highlightedPoint.toString())
      )
    }

    const drawShapes = (className, shapeType, drawEnterCb, drawMergeCb) => {
      let shapes = breakpoints.selectAll(`.${className}`).data(
        d =>
          (isDotHighlighted(d) && getPointType(d) === shapeType) ||
          (this.config.showPoints &&
            this.config.pointsSize !== 0 &&
            this.config.pointsSize !== 'hidden' &&
            getPointType(d) === shapeType)
            ? [d]
            : [],
        d => d.id
      )

      const shapesEnter = shapes
        .enter()
        .append('g')
        .attr('class', `point-shape ${className}`)
        .attr('stroke-width', 0)
      shapesEnter.call(drawEnterCb)
      shapes.exit().transition().duration(800).style('opacity', 0).remove()
      shapes = shapes.merge(shapesEnter)
      shapes
        .attr(
          'fill',
          !isDarkMode(this.config, this._parent.accessibilityOptions)
            ? COLORS.SKIN_BACKGROUND
            : COLORS.SKIN_BACKGROUND_DARK
        )
        .attr('stroke', d => this.color({ id: d.parentId }))
        .attr('stroke-width', d => (isDotHighlighted(d) ? this.config.highlightedPointStroke : 1))

      this.itemsEnterTrans.select('.break-point').select(`.${className}`).call(drawMergeCb)

      this.itemsTrans
        .select('.break-point')
        .attr(
          'transform',
          d =>
            `translate(${this.axes.x.func.scale()(d.id)},${this.axes.y.func.scale()(
              d.y1 || d.value
            )})`
        )
        .select(`.${className}`)
        .attr(
          'fill',
          !isDarkMode(this.config, this._parent.accessibilityOptions)
            ? COLORS.SKIN_BACKGROUND
            : COLORS.SKIN_BACKGROUND_DARK
        )
        .attr('stroke', d => this.color({ id: d.parentId }))
        .attr('stroke-width', d => (isDotHighlighted(d) ? this.config.highlightedPointStroke : 1))
        .call(drawMergeCb)

      this.itemsExitTrans.select(`.${className}`).attr('opacity', 0)
    }
    const SHAPES_DEFAULT_SIZE = this.config.pointsSize
      ? this.config.pointsSize === 'hidden'
        ? 0
        : this.config.pointsSize
      : 3
    //Circle
    drawShapes(
      'circle',
      'circle',
      sel => drawSvgCircle(sel, 0, 'enter'),
      sel =>
        drawSvgCircle(
          sel,
          d => (isDotHighlighted(d) ? this.config.highlightedPointSize : SHAPES_DEFAULT_SIZE),
          'edit'
        )
    )

    //Square
    drawShapes(
      'square',
      'square',
      sel => drawSvgSquare(sel, 0, 'enter'),
      sel =>
        drawSvgSquare(
          sel,
          d =>
            isDotHighlighted(d) ? this.config.highlightedPointSize * 2 : SHAPES_DEFAULT_SIZE * 2,
          'edit'
        )
    )
    //Triangles
    drawShapes(
      'triangle',
      'triangle',
      sel => drawSvgTriangle(sel, 0, 'enter'),
      sel =>
        drawSvgTriangle(
          sel,
          d =>
            isDotHighlighted(d) ? this.config.highlightedPointSize * 2 : SHAPES_DEFAULT_SIZE * 2,
          'edit'
        )
    )

    //Five point star
    drawShapes(
      'five-point-star',
      'fivePointStar',
      sel => drawSvgStar(sel, 0, 5, 'enter'),
      sel =>
        drawSvgStar(
          sel,
          d =>
            isDotHighlighted(d) ? this.config.highlightedPointSize * 2 : SHAPES_DEFAULT_SIZE * 2,
          5,
          'edit'
        )
    )

    //Six point star
    drawShapes(
      'six-point-star',
      'sixPointStar',
      sel => drawSvgStar(sel, 0, 6, 'enter'),
      sel =>
        drawSvgStar(
          sel,
          d =>
            isDotHighlighted(d) ? this.config.highlightedPointSize * 2 : SHAPES_DEFAULT_SIZE * 2,
          6,
          'edit'
        )
    )

    //Hexagon
    drawShapes(
      'hexagon',
      'hexagon',
      sel => drawSvgPolygon(sel, 0, 6, 'enter'),
      sel =>
        drawSvgPolygon(
          sel,
          d =>
            isDotHighlighted(d) ? this.config.highlightedPointSize * 2 : SHAPES_DEFAULT_SIZE * 2,
          6,
          'edit'
        )
    )

    //Pentagon
    drawShapes(
      'pentagon',
      'pentagon',
      sel => drawSvgPolygon(sel, 0, 5, 'enter'),
      sel => drawSvgPolygon(sel, SHAPES_DEFAULT_SIZE * 2, 5, 'edit')
    )

    //Plus
    drawShapes(
      'plus',
      'plus',
      sel => drawSvgPlus(sel, 0, 'enter'),
      sel => drawSvgPlus(sel, SHAPES_DEFAULT_SIZE * 2, 'edit')
    )

    /*
    let dots = breakpoints
      .selectAll('.dot')
      .data(d =>
        this.config.showPoints &&
        this.config.pointsSize !== 'hidden' &&
        this.config.pointsSize !== 0
          ? [null]
          : []
      )
    dots.exit().transition().duration(800).attr('r', 0).style('opacity', 0).remove()
    dots.enter().append('circle').attr('class', 'dot')

    this.itemsEnter
      .select('.break-point')
      .attr(
        'transform',
        d =>
          `translate(${this.axes.x.func.scale()(d.id)},${this.axes.y.func.scale()(
            d.y1 || d.value
          )})`
      )
      .select('.dot')
      .attr(
        'fill',
        !isDarkMode(this.config, this._parent.accessibilityOptions) ? COLORS.SKIN_BACKGROUND : COLORS.SKIN_BACKGROUND_DARK
      )
      .attr('stroke', d => this.color({ id: d.parentId }))
      .attr('r', 0)
    this.itemsEnterTrans.select('.break-point').select('.dot').attr('r', 3)
    this.itemsTrans
      .select('.break-point')
      .attr(
        'transform',
        d =>
          `translate(${this.axes.x.func.scale()(d.id)},${this.axes.y.func.scale()(
            d.y1 || d.value
          )})`
      )
      .select('.dot')
      .attr(
        'fill',
        !isDarkMode(this.config, this._parent.accessibilityOptions) ? COLORS.SKIN_BACKGROUND : COLORS.SKIN_BACKGROUND_DARK
      )
      .attr('stroke', d => this.color({ id: d.parentId }))
      .attr('r', this.config.pointsSize || 3)
    this.itemsExitTrans.select('.dot').attr('opacity', 0)
    */

    let minMaxValues = {}
    if (this.config.highlightMaxValue || this.config.highlightMinValue)
      if (this.config.highlightTotalMinMaxValue) {
        items.each(item => {
          if (!minMaxValues.min || !minMaxValues.max)
            minMaxValues = {
              min: { id: item.id, value: item.value },
              max: { id: item.id, value: item.value },
            }
          else {
            if (minMaxValues.max.value < item.value) {
              minMaxValues.max.value = item.value
              minMaxValues.max.parentId = item.parentId
              minMaxValues.max.id = item.id
            } else if (minMaxValues.min.value > item.value) {
              minMaxValues.min.value = item.value
              minMaxValues.min.parentId = item.parentId
              minMaxValues.min.id = item.id
            }
          }
        })
      } else {
        items.each(item => {
          if (!minMaxValues[item.parentId])
            minMaxValues[item.parentId] = {
              min: { id: item.id, value: item.value },
              max: { id: item.id, value: item.value },
            }
          else {
            if (minMaxValues[item.parentId].max.value < item.value) {
              minMaxValues[item.parentId].max.value = item.value
              minMaxValues[item.parentId].max.id = item.id
            } else if (minMaxValues[item.parentId].min.value > item.value) {
              minMaxValues[item.parentId].min.value = item.value
              minMaxValues[item.parentId].min.id = item.id
            }
          }
        })
      }

    const getLimitDots = (parentId, id) => {
      const result = []
      const minMaxValuesComputed = this.config.highlightTotalMinMaxValue
        ? minMaxValues
        : minMaxValues[parentId]
      if (this.config.highlightMaxValue) {
        if (
          minMaxValuesComputed?.max &&
          minMaxValuesComputed.max.id === id &&
          (!minMaxValuesComputed.max.parentId || minMaxValuesComputed.max.parentId === parentId)
        ) {
          result.push({ parentId: parentId, value: minMaxValuesComputed?.max })
        }
      }
      if (this.config.highlightMinValue) {
        if (
          minMaxValuesComputed?.min &&
          minMaxValuesComputed.min.id === id &&
          (!minMaxValuesComputed.min.parentId || minMaxValuesComputed.min.parentId === parentId)
        ) {
          result.push({ parentId: parentId, value: minMaxValuesComputed?.min })
        }
      }
      return result
    }

    let limitDots = breakpoints.selectAll('.limit-dot').data(
      d => getLimitDots(d.parentId, d.id),
      (_, i) => i
    )
    const limitDotsEnter = limitDots
      .enter()
      .append('circle')
      .attr('class', 'limit-dot')
      .attr('r', 12)
      .attr('opacity', 0)
    //.style('stroke-dasharray', 2)
    limitDots.exit().transition().duration(800).attr('r', 0).attr('opacity', 0).remove()
    limitDots = limitDots.merge(limitDotsEnter)

    limitDots
      .attr('fill', 'transparent')
      .attr('stroke', d => this.color({ id: d.parentId }))
      .transition()
      .duration(800)
      .attr('opacity', 1)

    let parentItems = this.parentItems.merge(this.parentItemsEnter)

    let lineLabels = parentItems.selectAll('.line-label').data(d => {
      return this.config.showLineLabel || this.config.showLineIcons ? [d] : []
    })
    lineLabels.exit().transition().style('opacity', 0).remove()
    let lineLabelsEnter = lineLabels.enter().append('g').attr('class', 'line-label')

    lineLabels = lineLabels.merge(lineLabelsEnter)

    //Labels
    //let centerSizes = { width: innerRadius * 1.6, height: innerRadius }

    //Check if has getLineHtml cb or preset option and return the html
    const customLineLabelHtml = getCustomLineLabelHtml(this.config)

    // IF DIV TEXTS (NOT GOOD for exporting, but good for advanced devs)
    let lineLabelsForeignObject = lineLabels
      .selectAll('.foreignObject')
      .data(d => (customLineLabelHtml ? [d] : []))

    const lineLabelsForeignObjectEnter = lineLabelsForeignObject
      .enter()
      .append('foreignObject')
      .attr('class', 'foreignObject')
    lineLabelsForeignObjectEnter.append('xhtml:div').attr('class', 'html-container')

    lineLabelsForeignObject.exit().transition().style('opacity', 0).remove()

    lineLabelsForeignObject = lineLabelsForeignObject.merge(lineLabelsForeignObjectEnter)

    //lineLabelsForeignObject.attr('width', 1).attr('height', 1)

    lineLabelsForeignObject
      .select('.html-container')
      .style('width', 'fit-content')
      .html(d => {
        return customLineLabelHtml.call(this, d)
      })

    //Labels && Icons
    const ICON_HEIGHT = 23
    const ICON_WIDTH = 23
    const ICON_OFFSET = 5

    let getIcon = d => {
      return (
        !!this.config.showLineIcons &&
        this.config.ItemsHelper.colorDict &&
        this.config.ItemsHelper.colorDict[d.id]?.icon?.icon &&
        this.config.ItemsHelper.colorDict[d.id]?.icon
      )
    }

    const self = this
    lineLabelsForeignObject.each(function (d) {
      const foreignObject = d3_select(this)
      // Create a new div element
      const itemRef = foreignObject.select('.html-container').node()

      document.body.append(itemRef)
      const boundingRect = itemRef.getBoundingClientRect()
      foreignObject.attr('width', boundingRect.width).attr('height', boundingRect.height)
      foreignObject.style(
        'transform',
        `translate(${self.config.showLineIcons && getIcon(d) ? ICON_WIDTH + ICON_OFFSET : 0}px, ${
          -boundingRect.height / 2
        }px)`
      )
      this.append(itemRef)
    })

    /*
    centerTextsDivs
      .select('.foreignObject')
      .select('.html-container')
      .transition()
      .style('padding-bottom', d => (this.config.centerValue && d.pos === 'top' ? '20px' : '0px'))
      .style('padding-top', d => (this.config.centerValue && d.pos === 'bottom' ? '20px' : '0px'))

    centerTextsDivs.transition().style('opacity', 1)
    */
    let lineLabelsText = lineLabels
      .selectAll('text')
      .data(d => (this.config.showLineLabel && !customLineLabelHtml ? [d] : []))
    lineLabelsText.exit().remove()
    let lineLabelsTextEnter = lineLabelsText
      .enter()
      .append('text')
      .attr('dominant-baseline', 'middle')
      .style('font-weight', 'bold')
    lineLabelsText = lineLabelsText.merge(lineLabelsTextEnter)

    lineLabelsText
      .attr(
        'font-size',
        this._parent.accessibilityOptions?.[ACCESSIBILITY_OPTIONS_KEYS.increaseTextSize]
          ? '18px'
          : '15px'
      )
      .text(d => d.id)
      .attr('fill', d => COLORS.mapAccessibleColor(this.color(d), this.config.skin))
      .transition()
      .duration(500)
      .attr(
        'transform',
        d =>
          `translate(${this.config.showLineIcons && getIcon(d) ? ICON_WIDTH + ICON_OFFSET : 0}, 0)`
      )

    //Icons
    let translate = [0, -ICON_HEIGHT / 2 - 2]

    let iconGroup = lineLabels
      .selectAll('.icon')
      .data(d => (getIcon(d)?.icon ? [{ id: d.id, ...getIcon(d) }] : []))
    iconGroup.exit().remove()
    let iconGroupEnter = iconGroup.enter().append('g').attr('class', 'icon')
    iconGroupEnter.style('opacity', 0).transition().duration(500).style('opacity', 1)
    iconGroup = iconGroup.merge(iconGroupEnter)
    iconGroup.attr('transform', `translate(${translate[0]}, ${translate[1]})`)
    iconGroup.html(d => (d ? d.icon.svg : null))
    let iconGroupSvg = iconGroup.select('svg')
    //Hay q hardcodear nuevos ids en los defs y en los use para hacer unicas las modificaciones
    iconGroupSvg
      .select('defs')
      .select('path')
      .attr('id', function (d) {
        return this.getAttribute('id') ? this.getAttribute('id') + `line-label-icon-${d.id}` : null
      })
    iconGroupSvg.select('use').attr('xlink:href', function (d) {
      return this.getAttribute('xlink:href')
        ? this.getAttribute('xlink:href') + `line-label-icon-${d.id}`
        : null
    })
    iconGroupSvg.selectAll('path, polygon').attr('fill', function () {
      let d = d3_select(this.parentNode).datum()
      return d && d.color && !d.icon.disableColorModifier ? d.color : this.getAttribute('fill')
    })
    iconGroupSvg.attr('height', `${ICON_HEIGHT}px`).attr('width', `${ICON_WIDTH}px`)
    iconGroupSvg.select('use').attr('fill', function (d) {
      return d && d.color && !d.icon.disableColorModifier ? d.color : this.getAttribute('fill')
    })

    layoutLabels(lineLabels, d =>
      this.axes.y.func.scale()(d.value[d.value.length - 1].y1 || d.value[d.value.length - 1].value)
    )

    let x0 = d3_max(this.data, d => d3_max(d.value, dd => this.axes.x.func.scale()(dd.id)))
    x0 = Math.min(this.axes.x.func.scale().range()[1], x0) + 12

    lineLabelsEnter
      .attr('transform', function () {
        return `translate(${x0},${this.y})`
      })
      .style('opacity', 0)
    lineLabels
      .transition()
      .duration(this._zooming ? 0 : 1000)
      .style('opacity', 1)
      .attr('transform', function () {
        return `translate(${x0},${this.y})`
      })

    //datum values
    let datumValues = this.itemsEnter
      .merge(this.items)
      .select('.break-point')
      .selectAll('.datum-value')
      .data(d => (this.config.showDatumValues ? [d] : []))

    this.itemsExit.select('.datum-value').transition().style('opacity', 0).remove()
    datumValues.exit().transition().style('opacity', 0).remove()

    datumValues = datumValues.merge(
      datumValues
        .enter()
        .append('text')
        .attr('class', 'datum-value')
        .style('opacity', 0)
        .style('font-weight', 'bold')
        .attr('fill', 'currentColor')
    )
    datumValues
      .attr(
        'font-size',
        this._parent.accessibilityOptions?.[ACCESSIBILITY_OPTIONS_KEYS.increaseTextSize]
          ? '18px'
          : '14px'
      )
      .text(d => this.valueWithUnit(d.value, 'y'))
      .transition()
      .style('opacity', 1)
    datumValues.attr('text-anchor', 'middle').attr('x', 0).attr('y', -8)
  }
}
